import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';

// Контекст шелла: страницы могут читать/обновлять tweaks (плотность, тема, компактный сайдбар).
// Экспортируем сам контекст — чтобы другие шеллы (TeacherShell и т.п.)
// могли поднимать тот же Provider, и страницы, написанные на useShell(),
// работали внутри них без правок.
export const ShellContext = createContext(null);
export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used inside <AppShell>');
  return ctx;
}

const STORAGE_KEY = 'crm2:tweaks';
const DEFAULT_TWEAKS = {
  theme: 'light',
  density: 'cozy',
  sidebarCompact: false,
};

function loadTweaks() {
  try { return { ...DEFAULT_TWEAKS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }; }
  catch { return DEFAULT_TWEAKS; }
}

// Реактивно отслеживаем «мобильный» брейкпоинт (≤768px), чтобы:
//   • не применять «компактный сайдбар» в мобильном drawer (там всегда
//     полный сайдбар с подписями);
//   • переключать раскладку страниц там, где это удобнее делать в JS.
export function useIsMobile(query = '(max-width: 768px)') {
  const [match, setMatch] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatch(e.matches);
    mq.addEventListener('change', handler);
    setMatch(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return match;
}

export default function AppShell() {
  const [tweaks, setTweaks] = useState(loadTweaks);
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.dataset.theme = tweaks.theme;
    document.documentElement.dataset.density = tweaks.density;
  }, [tweaks.theme, tweaks.density]);

  // Смена маршрута — закрываем мобильное меню.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Escape закрывает drawer.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const updateTweak = (key, value) => {
    setTweaks((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // На мобиле компактный сайдбар не применяем — drawer всегда полный.
  const compact = tweaks.sidebarCompact && !isMobile;

  const shellValue = {
    tweaks,
    updateTweak,
    isMobile,
    mobileNavOpen,
    toggleMobileNav: () => setMobileNavOpen((o) => !o),
    closeMobileNav: () => setMobileNavOpen(false),
  };

  return (
    <ShellContext.Provider value={shellValue}>
      <div className={
        'app'
        + (compact ? ' app--sidebar-compact' : '')
        + (mobileNavOpen ? ' app--mobile-nav-open' : '')
      }>
        <Sidebar compact={compact} />
        {/* Затемнение под drawer — клик закрывает. На десктопе скрыт через CSS. */}
        <div className="app__scrim" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
        <div className="main">
          <Outlet />
        </div>
      </div>
    </ShellContext.Provider>
  );
}
